import { Instagram, Twitter, Youtube, Linkedin, Facebook, Send, MessageCircle, ArrowUpRight } from "lucide-react";
import { SOCIALS, CONTACT, LEGAL, DOWNLOAD_LINK, LOGO_URL } from "@/lib/constants";

const SOCIAL_LIST = [
  { href: SOCIALS.instagram, label: "Instagram", Icon: Instagram },
  { href: SOCIALS.twitter, label: "X / Twitter", Icon: Twitter },
  { href: SOCIALS.youtube, label: "YouTube", Icon: Youtube },
  { href: SOCIALS.linkedin, label: "LinkedIn", Icon: Linkedin },
  { href: SOCIALS.facebook, label: "Facebook", Icon: Facebook },
  { href: SOCIALS.telegram, label: "Telegram", Icon: Send },
  { href: SOCIALS.whatsapp, label: "WhatsApp OTC", Icon: MessageCircle },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-slate-200 bg-slate-50 pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-[#3B6FBF]/30 bg-gradient-to-br from-[#3B6FBF]/10 via-white to-white p-8 sm:p-12 mb-16 relative overflow-hidden shadow-[0_20px_60px_-30px_rgba(59,111,191,0.3)]" data-testid="footer-cta">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#3B6FBF]/15 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-display font-black tracking-tighter text-3xl sm:text-4xl leading-tight text-slate-900">
                Start converting crypto<br /> the Pama way.
              </h3>
              <p className="mt-4 text-slate-600 max-w-md">
                Download PamaPay and join 10,000+ traders cashing out at the best rates in Nigeria.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <a
                href={DOWNLOAD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full px-6 py-3.5 transition-all"
                data-testid="footer-cta-download"
              >
                Download App <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href={SOCIALS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#3B6FBF] hover:bg-[#2f5ca3] text-white font-semibold rounded-full px-6 py-3.5 transition-all"
                data-testid="footer-cta-otc"
              >
                Bulk / OTC Desk
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          <div className="col-span-2">
            <a href="#top" className="flex items-center gap-2.5 mb-5">
              <img src={LOGO_URL} alt="PamaPay" className="h-10 w-10 rounded-lg" />
              <span className="font-display font-black text-xl text-slate-900">PamaPay</span>
            </a>
            <p className="text-sm text-slate-600 max-w-xs leading-relaxed">
              The fastest, most rewarding way to convert your crypto to Nigerian Naira. Built by traders, for traders.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {SOCIAL_LIST.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#3B6FBF] hover:border-[#3B6FBF]/40 hover:bg-[#3B6FBF]/5 transition-all"
                  data-testid={`footer-social-${label.toLowerCase().replace(/[\s\/]/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <FooterCol title="Product" links={[
            { label: "Crypto to Naira", href: DOWNLOAD_LINK },
            { label: "Rates", href: "#rates" },
            { label: "Features", href: "#features" },
            { label: "OTC Bulk Desk", href: SOCIALS.whatsapp },
          ]} />

          <FooterCol title="Community" links={[
            { label: "Telegram", href: SOCIALS.telegram },
            { label: "X / Twitter", href: SOCIALS.twitter },
            { label: "Instagram", href: SOCIALS.instagram },
            { label: "YouTube", href: SOCIALS.youtube },
          ]} />

          <FooterCol title="Legal" links={[
            { label: "Help Center", href: "/#faq" },
            { label: "Terms of Use", href: "/terms-of-use" },
            { label: "Privacy Policy", href: "/privacy-policy" },
            { label: "Contact", href: `mailto:${CONTACT.email}` },
          ]} />
        </div>

        <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500">
          <div data-testid="footer-copyright">
            {CONTACT.company} © {new Date().getFullYear()}. All rights reserved.
          </div>
          <div className="flex items-center gap-1.5 font-mono-data">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Systems operational
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  const isInternal = (h) => h.startsWith("/") && !h.startsWith("//");
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-4 font-semibold">{title}</div>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            <a
              href={l.href}
              target={isInternal(l.href) || l.href.startsWith("#") || l.href.startsWith("mailto") ? "_self" : "_blank"}
              rel="noopener noreferrer"
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              data-testid={`footer-link-${l.label.toLowerCase().replace(/[\s\/]/g, "-")}`}
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
